import type { Modifiers } from './doClick';
import type { StepQuirks } from 'clickim/src/common/models/step';
import type { IMouseEvent } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;
interface DoDragPathEventData {
    isRoot?: boolean;
    locatedElement: ILocatedElement;
    events: IMouseEvent[];
    quirks?: StepQuirks;
    modifiers?: Partial<Modifiers>;
    button: number | undefined;
    isDrag: boolean;
    allEventsOnSameElement: boolean | undefined;
}

interface Context extends Pick<DoDragPathEventData, 'events' | 'quirks' | 'isDrag' | 'allEventsOnSameElement'> {
    eventIndex: 0;
    element: Element;
    eventType: never;
    eventData: never;
    stepId: never;
    testResultId: never;
    isDoubleClick: never;
    useRecordedMousedown: never;
    trackActiveElement: never;
}

export const doDragPath = function (eventData: DoDragPathEventData, done: (data: { status: 'done' | 'failed'; result?: string; success: boolean }) => void) {
    const eventConstructorSupported = typeof Event === 'function';
    const pointerEventConstructorSupported = typeof PointerEvent === 'function';
    const MAX_EVENT_TIMEOUT = 40; //max msec between events

    window.__unloadNavigator = resolve;

    window.addEventListener('unload', window.__unloadNavigator);

    function resolve(result?: string) {
        const status = {
            status: 'done' as const,
            result,
            success: true,
        };
        done(status);
    }

    function reject(result: string) {
        result ||= '';
        const status = {
            status: 'failed' as const,
            result,
            success: false,
        };

        done(status);
    }

    function dispatchMouseOver(element: Element) {
        const event = eventConstructorSupported ? new Event('mouseover', { composed: true }) : document.createEvent('Events');
        event.initEvent('mouseover', true, true);

        element.dispatchEvent(event);
    }

    function dispatchMouseMove(element: Element) {
        const modifiers = {};
        const rect = element.getBoundingClientRect();
        const clientX = rect.left + (rect.width / 2);
        const clientY = rect.top + (rect.height / 2);
        const button = 0;
        const eventType = 'mousemove';
        const event = createMouseEvent(eventType, modifiers, clientX, clientY, button);

        element.dispatchEvent(event);
    }

    function getEventPosition(userEvent: IMouseEvent, element: Element) {
        function isWithinBounds(start: number, end: number, point: number) {
            return (point > start) && (point < end);
        }

        const pointerPosition = userEvent.pointerPosition || {} as Partial<NonNullable<IMouseEvent['pointerPosition']>>;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (context.isDrag) {
            return { x: pointerPosition.originX || 0, y: pointerPosition.originY || 0 };
        }
        const rect = element.getBoundingClientRect();
        const clientX = pointerPosition.originX && isWithinBounds(rect.left, rect.left + rect.width, pointerPosition.originX) ? pointerPosition.originX : rect.left + (rect.width / 2);
        const clientY = pointerPosition.originY && isWithinBounds(rect.top, rect.top + rect.height, pointerPosition.originY) ? pointerPosition.originY : rect.top + (rect.height / 2);
        return { x: clientX, y: clientY };
    }

    function getEventDictionary(modifiers: Partial<Modifiers>, clientX: number, clientY: number): PointerEventInit {
        return {
            screenX: 0,
            screenY: 0,
            clientX,
            clientY,
            ctrlKey: Boolean(modifiers.ctrl),
            altKey: Boolean(modifiers.alt),
            shiftKey: Boolean(modifiers.shift),
            metaKey: Boolean(modifiers.meta),
            bubbles: true,
            cancelable: true,
            composed: true,
        };
    }

    function createPointerEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number, button: number) {
        if (pointerEventConstructorSupported) {
            const eventProperties = getEventDictionary(modifiers, clientX, clientY);
            eventProperties.pointerType = 'mouse';
            eventProperties.isPrimary = true;
            return new window.PointerEvent(eventType, eventProperties);
        }
        const event = document.createEvent('PointerEvent');
        // @ts-expect-error This smells like some IE hack that should be dropped
        event.initPointerEvent(
            eventType,
            true, /* bubbles */
            true, /* cancelable */
            document.defaultView, /* view */
            1, /* detail */
            0, /* screenX */
            0, /* screenY */
            clientX, /* clientX */
            clientY, /* clientY */
            Boolean(modifiers.ctrl), /* ctrl */
            Boolean(modifiers.alt), /* alt */
            Boolean(modifiers.shift), /* shift */
            Boolean(modifiers.meta), /* meta */
            button, /* button */
            document.body ? document.body.parentNode : document.documentElement,
            0, /* offsetYArg */
            0, /* offsetXArg */
            0, /* widthArg */
            0, /* heightArg */
            0, /* pressure */
            0, /* rotation */
            0, /* tiltX */
            0, /* tiltY */
            0, /* pointerIdArg */
            'mouse', /* pointerType */
            0, /* hwTimestampArg */
            true, /* isPrimary */
        );

        return event;
    }

    function createMouseEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number, button: number) {
        const event = eventConstructorSupported ? new MouseEvent('click', { composed: true }) : document.createEvent('MouseEvents');
        event.initMouseEvent(
            eventType,
            true, /* bubbles */
            true, /* cancelable */
            document.defaultView!, /* view */
            1, /* detail */
            0, /* screenX */
            0, /* screenY */
            clientX, /* clientX */
            clientY, /* clientY */
            Boolean(modifiers.ctrl), /* ctrl */
            Boolean(modifiers.alt), /* alt */
            Boolean(modifiers.shift), /* shift */
            Boolean(modifiers.meta), /* meta */
            button, /* button */
            document.body ? document.body.parentNode : document.documentElement);
        return event;
    }

    function getMouseEvent(userEvent: IMouseEvent, context: Context) {
        const pointerEvents = ['pointerup', 'pointerdown', 'pointermove'];
        // @ts-expect-error we never set modifiers to the context :(
        const modifiers = context.modifiers || {};
        const pos = getEventPosition(userEvent, context.element);
        // @ts-expect-error we never set button to the context :(
        const button = context.button || 0;
        const eventType = userEvent.event;
        if (pointerEvents.includes(eventType)) {
            return createPointerEvent(eventType, modifiers, pos.x, pos.y, button);
        }
        return createMouseEvent(eventType, modifiers, pos.x, pos.y, button);
    }

    function shouldSkipEvent(currentEvent: MouseEvent, context: Context) {
        function isClickInDragAndDrop() {
            // @ts-expect-error not sure where this property comes from
            return currentEvent.event === 'click' &&
                context.isDrag &&
                !context.allEventsOnSameElement;
        }
        return isClickInDragAndDrop();
    }

    function executeAsyncNextEventRecursive(index: number, context: Context, _done: VoidFunction) {
        try {
            const event = getMouseEvent(context.events[index], context);
            if (!shouldSkipEvent(event, context)) {
                context.element.dispatchEvent(event);
            }
        } catch (ignore) { /* ignored */ }

        if (index + 1 === context.events.length) {
            _done();
        } else {
            const delay = Math.min(context.events[index + 1].timeStamp! - context.events[index].timeStamp!, MAX_EVENT_TIMEOUT);
            setTimeout(() => {
                executeAsyncNextEventRecursive(index + 1, context, _done);
            }, delay);
        }
    }

    function executeAsyncEventSequence(context: Context, _done: VoidFunction) {
        executeAsyncNextEventRecursive(0, context, () => {
            if (window.__unloadNavigator) {
                window.removeEventListener('unload', window.__unloadNavigator);
            }
            _done();
        });
    }

    const element = eventData.isRoot ? document.documentElement : getLocatedElement(eventData.locatedElement)!;

    const context: Context = {
        eventIndex: 0,
        element,
        events: eventData.events,
        // @ts-expect-error unused & invalid
        eventType: eventData.eventType,
        // @ts-expect-error unused & invalid
        eventData: eventData.eventData,
        // @ts-expect-error unused & invalid
        stepId: eventData.id,
        // @ts-expect-error unused & invalid
        testResultId: eventData.testResultId,
        quirks: eventData.quirks,
        // @ts-expect-error unused & invalid
        isDoubleClick: eventData.isDoubleClick,
        isDrag: eventData.isDrag,
        // @ts-expect-error unused & invalid
        useRecordedMousedown: eventData.useRecordedMousedown,
        // @ts-expect-error unused & invalid
        trackActiveElement: eventData.trackActiveElement,
        allEventsOnSameElement: eventData.allEventsOnSameElement,
    };

    if (!context.element) {
        reject('element not found');
        return;
    }

    dispatchMouseOver(context.element);
    dispatchMouseMove(context.element);

    executeAsyncEventSequence(context, () => {
        resolve();
    });
};
